import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  PageTitleAndSubTitle,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useNavigate } from 'react-router-dom';
import {
  TERM_COMPILER_INTERNAL,
  NEW_TERM_COMPILER,
} from '../../core-utils/routes';
import ComparisonTable from '../../_components/atoms/ComparisonTable';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import Loader from '../../_components/molecules/Loader';
import { Pagination } from '@mui/material';
import NoDoc from '../../_components/atoms/Icons/NoDocuments';
import {
  extractTermCompilerMetaData,
  fetchUseCases,
  formatCompilationTableData,
  updateCompiler,
} from '../../core-utils/helper-utils/termCompilerHelper';
import { DueDiligenceDataType } from '../../core-utils/helper-utils/recommendedAppsHelper';
import AutocompleteWithCheckBoxes from '../../_components/atoms/Autocomplete';
import {
  Button,
  CheckBox,
  SimpleSearch,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { getTextWithToolTip } from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../_components/atoms/Snackbar';
import { useStores } from '../../stores';

export interface Document {
  document_name: string;
}
export interface compilationDataProps {
  _id: string;
  eg_nid?: string;
  compiler_name?: string;
  use_case?: any;
  description?: string;
  contracts?: string[];
  plan_docs?: string[];
  selected_terms?: any[];
  status: 'Ready' | 'Processing' | 'Failed Compilation' | 'Terms Not Selected';
  updatedAt: string;
  updatedBy: string;
  termCount: Number;
}

const useStyles = makeStyles()({
  root: {
    width: '1480px',
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 60px',
  },
  titleAndSubtitle: {
    padding: '32px 0px',
  },
  hr: {
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
    height: '66px',
    backgroundColor: patientAccessTheme.palette.status.white,
    borderTop: '1px solid ' + patientAccessTheme.palette.grey[300],
    padding: '20px 16px 0px 0px',
  },
  noDataBottomCardContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '300px',
  },
  filterGrid: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

const TermCompilerPage: React.FC<{}> = observer(({}) => {
  const { classes } = useStyles();
  const message = RECOMMENDED_APPS.TERM_COMPILER;
  const navigate = useNavigate();
  const loading = <Loader />;
  const [useCases, setUseCases] = useState<any>([]);
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const { openSnackbar, snackbarState, handleCloseSnackBar } = useSnackbar();

  const [compilationData, setCompilationData] = useState<
    compilationDataProps[]
  >([]);
  const [mainLoading, setMainLoading] = useState(false);

  const [pageCount, setPageCount] = useState(10);
  const [tablePageNumber, setTablePageNumber] = useState(1);
  const pageLimit = 10;
  const EmployerGroupStore = useStores().employerGroupStore;
  const LoginPageStore = useStores().loginPageStore;

  // function
  const handlePagination = (_event: ChangeEvent<unknown>, page: number) => {
    setTablePageNumber(page);
  };

  const handleRowClick = (
    _eg_nid: string,
    status: DueDiligenceDataType['status'] | compilationDataProps['status'],
    _contract_ids: string[] | undefined,
    _company_name: string | undefined,
    _vendor_type: string | undefined,
    comparison_id: compilationDataProps['_id'],
  ) => {
    if (status === 'Ready') {
      navigate(TERM_COMPILER_INTERNAL, {
        state: { term_compiler_id: comparison_id },
      });
    }
  };

  const handleMenu = async (row: any, option: string) => {
    switch (option) {
      case 'View':
        if (row.status === 'Ready') {
          navigate(TERM_COMPILER_INTERNAL, {
            state: { term_compiler_id: row._id },
          });
        }
        break;
      case 'Regenerate':
        const data = {
          term_compiler_id: row._id,
          eg_nid: row.eg_nid,
          selected_terms: row.selected_terms,
          contracts: row.contract_ids.map((con: any) => con._id),
          plan_docs: row.plan_docs.map((plan: any) => plan._id),
        };
        row?.selected_terms?.length > 0
          ? extractTermCompilerMetaData(data, openSnackbar)
          : openSnackbar('Terms Not Selected', 'error', 2000);
        break;
      case 'Delete':
        await updateCompiler({ archive: true }, row._id, openSnackbar);
        getAllCompilations();
        break;
      default:
        break;
    }
  };

  const handleSearchChange = (event: any) => {
    setSearchText(event.target.value);
  };

  // Apis

  const getAllCompilations = async () => {
    try {
      setMainLoading(true);
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getAllCompilations,
        {
          page: tablePageNumber,
          limit: pageLimit,
          eg_nid: EmployerGroupStore.allEmployerGroupsData.map(
            (item: any) => item.eg_nid,
          ),
          selectedUseCases,
          searchText,
        },
      );
      const compilationData = response?.data?.data?.data || [];
      const totalCount = response?.data?.data?.totalCount || 0;
      const formattedData = formatCompilationTableData(compilationData);
      setCompilationData(formattedData);
      setPageCount(Math.ceil(totalCount / pageLimit));
    } catch (err) {
      throw err;
    } finally {
      setMainLoading(false);
    }
  };

  useEffect(() => {
    getAllCompilations();
  }, [tablePageNumber, searchText, selectedUseCases]);

  useEffect(() => {
    fetchUseCases(setUseCases, {
      eg_nid: EmployerGroupStore.allEmployerGroupsData.map(
        (item: any) => item.eg_nid,
      ),
      organization: LoginPageStore.userResponse.organizationName,
    });
  }, []);

  return (
    <>
      <Grid container className={classes.root}>
        <Grid className={classes.titleAndSubtitle}>
          <PageTitleAndSubTitle
            title={message.TITLE}
            subTitle={message.SUBTITLE}
            customButton={true}
            customButtonText="Add New Compilation"
            customButtonClick={() => {
              navigate(NEW_TERM_COMPILER);
            }}
            customButtonWidth="180px"
            customButtonVariant={'contained'}
          />
        </Grid>
        <Grid className={classes.filterGrid}>
          <TextField
            placeholder={'Search Compilation'}
            height="40px"
            width="324px"
            startAdornment={
              <SimpleSearch
                height="20px"
                width="20px"
                stroke={patientAccessTheme.palette.text.lowemphasis}
              />
            }
            showStartAdornment={true}
            value={searchText}
            onChange={handleSearchChange}
            inputPropsStyle={{ margin: '1rem 0' }}
          />
          <AutocompleteWithCheckBoxes
            placeholder={'Filter By UseCase'}
            width="300px"
            height="40px"
            options={useCases.map((temp: any) => temp.use_case)}
            handleChange={(event, selectedNames) =>
              setSelectedUseCases(selectedNames)
            }
            getOptionLabel={(option: any) => option}
            renderOption={(option: any, { selected }: any) => {
              return (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <CheckBox checked={selected} id="" name="" color="primary" />
                  {getTextWithToolTip(option, 25)}
                </Box>
              );
            }}
          />
        </Grid>
        <CardComponent
          title={message.TABLE_TITLE}
          subTitle={''}
          mainGridStyles={{
            marginTop: '20px',
          }}
          body={
            <Grid>
              <Grid
                style={{
                  marginTop: '10px',
                }}
              >
                {mainLoading ? (
                  loading
                ) : compilationData.length > 0 ? (
                  <Grid>
                    <ComparisonTable
                      columns={message.COLUMNS}
                      rowsData={compilationData}
                      handleRowClick={handleRowClick}
                      showMenu={true}
                      menuOptions={message.MENU}
                      onMenuSelect={handleMenu}
                    />
                    <Box className={classes.pagination}>
                      <Pagination
                        count={pageCount}
                        page={tablePageNumber}
                        boundaryCount={1}
                        color="primary"
                        size="small"
                        shape="circular"
                        onChange={handlePagination}
                      />
                    </Box>
                  </Grid>
                ) : (
                  <Grid className={classes.noDataBottomCardContainer}>
                    <NoDoc />
                    <Typography
                      fontSize="16px"
                      fontType="semiBold"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      margin="16px 0px 10px 0px"
                    >
                      {message.CARD.noData.title}
                    </Typography>
                    <Button
                      label={'Add New Compilation'}
                      color="primary"
                      width="auto"
                      height="auto"
                      onClick={() => {
                        navigate(NEW_TERM_COMPILER);
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          }
        />
      </Grid>
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
});

export default TermCompilerPage;
